import { useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { OfflineQueueService } from './OfflineQueueService';
import { createOrder, createTakeawayOrder, updateOrderStatus, addItemsToOrder } from './OrderService';
import { createPayment } from './PaymentService';

export function useOfflineSyncManager() {
  const syncOfflineOperations = useCallback(async () => {
    if (!navigator.onLine) return;

    const queue = await OfflineQueueService.getQueue();
    if (queue.length === 0) return;

    toast.info(`Sincronizando ${queue.length} operaciones offline...`);
    let successCount = 0;

    for (const op of queue) {
      if (op.retries > 3) {
        // Skip operations that failed too many times, to avoid blocking the queue permanently
        console.warn('Skipping stuck operation', op);
        continue;
      }

      try {
        switch (op.type) {
          case 'CREATE_ORDER':
            await createOrder(op.payload);
            break;
          case 'CREATE_TAKEAWAY_ORDER':
            await createTakeawayOrder(op.payload);
            break;
          case 'UPDATE_ORDER_STATUS':
            await updateOrderStatus(op.payload.id, op.payload.status);
            break;
          case 'ADD_ITEMS':
            await addItemsToOrder(op.payload.id, { items: op.payload.items });
            break;
          case 'CREATE_PAYMENT':
            await createPayment(op.payload);
            break;
          default:
            console.warn('Unknown offline operation type', op.type);
        }
        
        await OfflineQueueService.removeOperation(op.id);
        successCount++;
      } catch (err: any) {
        console.error('Offline sync error for op', op.id, err);
        // Only retry if it's a network error. If it's 400 or 500, we probably shouldn't retry infinitely.
        const isNetworkErr = !err.response || err.code === 'ERR_NETWORK';
        if (isNetworkErr) {
          await OfflineQueueService.incrementRetry(op.id);
        } else {
          // If the server rejected it (e.g. invalid status transition), drop it to avoid infinite loops
          await OfflineQueueService.removeOperation(op.id);
        }
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} operaciones sincronizadas exitosamente.`);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      syncOfflineOperations();
    };

    window.addEventListener('online', handleOnline);

    // Initial check just in case we opened the app while online but had pending operations
    if (navigator.onLine) {
      syncOfflineOperations();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncOfflineOperations]);

  return { syncOfflineOperations };
}
